import React, { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Layout, LayoutSplashScreen } from "../_metronic/layout";
import { AdminLayout } from "../_metronic/layout/components/AdminLayout";
import ProtectedRoute from "./base/components/ProtectedRoute";
import AuthService from "./base/services/authentication.service";
import { AuthPage } from "./pages/auth/routes";
import { UserRolesEnum } from "./pages/auth/userRoles.enum";
import { Error } from "./pages/error/Error.js";
import LoginForAfrica from "./pages/auth/forAfricaLogin/LoginForAfrica.jsx";
import SuccessPaymentInfo from "./pages/account/SuccessPaymentInfo.js";
import FailedPaymentInfo from "./pages/account/FailedPaymentInfo.js";
import ProfileInfo from "./pages/account/ProfileInfo.js";
import AccountSummary from "./pages/account/AccountSummary.js";
import LoginInfo from "./pages/account/LoginInfo.js";
import OrganizationProfileSelf from "./pages/workspace/organizationList/OrganizationProfileSelf.js";
import TeamMembersList from "./pages/workspace/organizationList/TeamMembersList.js";
import ManagerProfile from "./pages/workspace/organizationList/ManagerProfile.js";
import HelpDeskList from "./pages/workspace/helpDesk/HelpDeskList.js";
import HelpDeskDetails from "./pages/workspace/helpDesk/HelpDeskDetails.js";
import NotificationsPage from "./pages/notifications/NotificationsPage.js";
import { useTranslation } from "./i18n/LanguageContext";

const AdminPages = lazy(() => import("./pages/admin/routes"));
const WorkspacePages = lazy(() => import("./pages/workspace/routes"));
const ApproverPages = lazy(() => import("./pages/approver/routes"));

export function Routess() {
   const { t } = useTranslation();
   const [isAuthenticated, setisAuth] = useState(() => AuthService.isAuthenticated());

   const location = useLocation();

   useEffect(() => {
      setisAuth(AuthService.isAuthenticated());
   }, [location, setisAuth]);

   return (
      <Suspense fallback={<LayoutSplashScreen />}>
         <Routes>
            {!isAuthenticated ? (
               /*Render auth page when user at `/auth` and not authorized.*/
               <>
                  <Route path="/*" element={<AuthPage />} />
               </>
            ) : (
               //  :

               /*Otherwise Navigate to root page (`/`)*/
               <>
                  {/* ADD Routes for admin and workspace when route empty */}
                  {/* <Route path="/" element={<Navigate to="/admin/workspace" />} />
                  <Route path="/auth/*" element={<Navigate to="/admin" />} /> */}

                  {AuthService.isAuthorized([UserRolesEnum.Approver]) && (
                     <Route element={<AdminLayout />}>
                        <Route path="/approver/*" element={<ApproverPages />} />
                        <Route path="/approver/notifications" element={<NotificationsPage />} />
                     </Route>
                  )}
                  <Route element={<Layout />} /* Main Content with Layout */>
                     {/* module */}
                     <Route
                        element={
                           <ProtectedRoute
                              allowedRoles={[
                                 UserRolesEnum.SuperAdmin,
                                 UserRolesEnum.InternationalOrganization,
                                 UserRolesEnum.NationalOrganization,
                                 UserRolesEnum.Manager,
                                 UserRolesEnum.Donor,
                                 UserRolesEnum.Staff,
                                 UserRolesEnum.Consultant
                              ]}
                           />
                        }
                     >
                        <Route path="/*" element={<WorkspacePages />} />
                     </Route>
                     {/* module */}
                  </Route>
                  {!AuthService.isAuthorized([UserRolesEnum.Approver]) && (
                     <Route element={<AdminLayout />}>
                        {
                           AuthService.isAuthorized([UserRolesEnum.SuperAdmin])
                           ?
                           <Route path="/admin/*" element={<AdminPages />} />
                           :
                           <>
                              <Route path="/workspace" element={<AdminPages />} />

                              <>
                              {
                                 !AuthService.isAuthorized([UserRolesEnum.SuperAdmin]) &&
                                 (
                                    <>
                                       <Route path="/profile-information" element={<ProfileInfo />} />
                                       <Route
                                          path="/organization-profile"
                                          element={<OrganizationProfileSelf />}
                                       />
                                       <Route
                                          path="/organizations/members"
                                          element={<TeamMembersList />}
                                       />
                                       <Route path="/login-information" element={<LoginInfo />} />
                                       <Route path="/notifications" element={<NotificationsPage />} />
                                       {AuthService.isAuthorized([UserRolesEnum.Manager, UserRolesEnum.Staff]) && (
                                          <Route path="/my-profile" element={<ManagerProfile />} />
                                       )}
                                       {
                                          AuthService.isAuthorized([UserRolesEnum.NationalOrganization]) &&
                                          <Route path="/account-summary" element={<AccountSummary />} />
                                       }
                                       {
                                          // Help desk needs the AdminLayout sidebar (Implementation/
                                          // Organization Management/Help desk) for org roles - without
                                          // these explicit routes, these exact URLs fall through to the
                                          // "/*" catch-all under <Layout> in the block above, which
                                          // renders the unrelated mainMenu sidebar (Assessments/Results/
                                          // Organizations) instead. Scoped to org roles only so Manager/
                                          // Staff/Donor/Consultant keep using the catch-all as before.
                                          (AuthService.isAuthorized([UserRolesEnum.NationalOrganization]) ||
                                             AuthService.isAuthorized([
                                                UserRolesEnum.InternationalOrganization
                                             ])) && (
                                             <>
                                                <Route
                                                   path="/workspace/help-desk/list"
                                                   element={<HelpDeskList />}
                                                />
                                                <Route
                                                   path="/workspace/help-desk/details/:id"
                                                   element={<HelpDeskDetails />}
                                                />
                                             </>
                                          )
                                       }
                                    </>
                                 )
                              }
                              </>
                           </>
                        }
                        {/* <Route path="/admin/*" element={<AdminPages />} /> */}
                     </Route>
                  )}
               </>
            )}
            <Route path="/success-payment-information" element={<SuccessPaymentInfo />} />
            <Route path="/failed-payment-information" element={<FailedPaymentInfo />} />
            <Route
               path="/unauthorized"
               element={
                  <Error
                     status="401"
                     title="401"
                     subTitle={t("error-unauthorized-text")}
                     hasBackButton
                  />
               }
            />
            <Route
               path="/404"
               element={
                  <Error
                     status="404"
                     title="404"
                     subTitle={t("error-page-not-found-text")}
                     hasBackButton
                  />
               }
            />
            <Route
               path="/500"
               element={
                  <Error
                     title={t("error-oops-title")}
                     subTitle={t("error-something-wrong-text")}
                     hasBackButton
                  />
               }
            />
            <Route
               path="*"
               element={
                  <Error
                     title={t("error-oops-title")}
                     subTitle={t("error-something-wrong-text")}
                     hasBackButton
                  />
               }
            />
         </Routes>
      </Suspense>
   );
}
